
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { NavigationGuardNext, Route } from 'vue-router';
import { StatusId } from '@/lib/enum';
import { getPdfDisplayOptions, getStatusList } from '@/lib/formItem';
import { store } from '@/store/index';
import {
  dispatchDeleteCompanySdses,
  dispatchGetCompanySds,
  dispatchGetCompanySdsPdf,
  dispatchGetCompanySdses,
  dispatchGetCompanySdsesCount,
  dispatchUpdateReconfirmationSds,
} from '@/store/main/actionsMain';
import { useSdsFilter } from '@/composables/useSdsFilter';
import {
  readAssertedCompany,
  readAssertedCompanyId,
} from '@/store/main/getters';
import { commitSetCompany } from '@/store/main/mutations';
import SdsSearchForm from '@/views/dashboard/SdsSearchForm.vue';
import SdsBottomSheet from '@/views/dashboard/bottomSheet/SdsBottomSheet.vue';
import SdsReconfirmationTable from '@/views/pages/sdsIndexError/SdsReconfirmationTable.vue';
import SdsTableDrawer from '@/views/dashboard/sdsDrawer/SdsTableDrawer.vue';
import { useDepartmentComposable } from '@/composables/useDepartment';
import { ReconfirmationSds, SdsResponse } from '@/interfaces/api/general/sds';
import { Department } from '@/interfaces/model/general/department';
import { TagResponse } from '@/interfaces/model/general/tag';
import { useSelectedItemsComposable } from '@/composables/useSelectedItems';
import { useUserComposable } from '@/composables/useUser';
import { defineMessages } from '@/lib/i18n';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';
import { useSdsComposable } from '@/composables/useSds';

const $t = i18n.t.bind(i18n);
const pdfDisplayOptions = getPdfDisplayOptions();
const statusList = getStatusList();

const m = defineMessages({
  reset: 'リセット',
});

const DefaultReconfirmationSds: ReconfirmationSds = {
  comment: '',
  comment_created_at: '',
  departments: [],
  tags: [],
  assignee_users: [],
  created_user: null,
  dynamic_list_result: [],
  product_code_list: [],
  attachments: [],
  sds_section8: {
    id: 0,
    sds_id: '',
    skin_protection: '',
    protective_equipment_of_skin: 'NONE',
    created_at: '',
    updated_at: '',
    deleted_at: null,
  },
  sds_section14: {
    id: 0,
    sds_id: '',
    un_number_list: [],
    created_at: '',
    updated_at: '',
    deleted_at: null,
  },
  sds_products: [],
  sds_type: {
    id: 0,
    sds_id: '',
    language_id: 0,
    is_embedded: false,
    fee: 0,
    comment: '',
    created_at: '',
    updated_at: '',
    deleted_at: null,
  },
  assistant_log_cross_checks: [],
  id: 0,
  uuid: '',
  replaced_name: '',
  name: '',
  note: '',
  admin_note: '',
  status_id: 0,
  timestamp: 0,
  created_by: '',
  updated_by: '',
  company_id: '',
  published_at: null,
  revised_at: null,
  supplier_confirmed_at: null,
  assessed_at: null,
  created_at: '',
  deleted_at: null,
  updated_at: '',
};

export default defineComponent({
  components: {
    SdsBottomSheet,
    SdsReconfirmationTable,
    SdsTableDrawer,
    SdsSearchForm,
  },
  beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext): void {
    if (this.isEditing) {
      const isAllowed: boolean = window.confirm(
        $t(sharedMessages.confirmLeave)
      );
      if (isAllowed) {
        next();
      }
    } else {
      next();
    }
  },
  setup() {
    const showSdsDetailView = ref<boolean>(false);
    const pdfName = ref<string>('');
    const reconfirmationSds = ref<ReconfirmationSds[]>([]); // SDS一覧表示用データ
    const activeSds = ref<ReconfirmationSds>(DefaultReconfirmationSds);
    const keywordList = ref<string[]>([]);
    const page = ref<number>(1);
    const limit = ref<number>(50);
    const productFormItems = ref<Array<{ text: string; value: number }>>([]);
    const currentIndex = ref(0);
    const isLoadingSdses = ref<boolean>(false);
    const isLoadingSds = ref<boolean>(false);
    const isEditing = ref<boolean>(false);
    const {
      getCompanyDepartments,
      companyDepartments,
      visibleCompanyDepartmentsByOwnOrRole,
    } = useDepartmentComposable();

    const { total, pdfBlob, updateCompanySds } = useSdsComposable();
    const [
      selectedSdses,
      isSelectedAllSdses,
      isSelectedIndeterminateSdses,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _isSelectedWholeSdses,
      toggleSelectedSds,
      toggleAllSelectedSdses,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _toggleWholeSelectedSdses,
      resetSelectedSdses,
    ] = useSelectedItemsComposable<ReconfirmationSds>(
      reconfirmationSds.value,
      (sds1, sds2) => sds1.uuid === sds2.uuid
    );
    const { statusOptions } = useSdsFilter([], [], companyDepartments);
    const { users, getCompanyUsers } = useUserComposable();

    watch(showSdsDetailView, (next) => {
      if (next === false) {
        activeSds.value = DefaultReconfirmationSds;
      }
    });

    const bindSdsData = (item: ReconfirmationSds): void => {
      activeSds.value = item;
    };

    const resetPagenation = (): void => {
      page.value = 1;
    };

    const resetSearchForm = (): void => {
      keywordList.value = [];
    };

    const hasPrevSds = computed((): boolean => {
      return currentIndex.value > 0;
    });

    const hasNextSds = computed((): boolean => {
      return currentIndex.value < reconfirmationSds.value.length - 1;
    });

    const showPrevSds = async (): Promise<void> => {
      const index = currentIndex.value - 1;

      showSdsDetail(reconfirmationSds.value[index], index);
    };

    const showNextSds = async (): Promise<void> => {
      const index = currentIndex.value + 1;

      showSdsDetail(reconfirmationSds.value[index], index);
    };

    const showSdsDetail = async (
      item: ReconfirmationSds,
      index: number
    ): Promise<void> => {
      // 一旦ビューを出す
      showSdsDetailView.value = true;
      isLoadingSds.value = true;
      try {
        await Promise.all([
          fetchCompanySds(item.uuid),
          fetchPdfData(item.uuid),
        ]).catch((e) => {
          console.log(e);
        });

        currentIndex.value = index;

        productFormItems.value.splice(0);
        productFormItems.value.push(
          ...item.sds_products.map((x, idx) => {
            return {
              text: x.name,
              value: idx,
            };
          })
        );
      } finally {
        isLoadingSds.value = false;
      }
    };

    const fetchPdfData = async (fileUuid: string): Promise<void> => {
      const res = await dispatchGetCompanySdsPdf(store, {
        companyId: readAssertedCompanyId(store),
        fileUuid: fileUuid,
      });
      if (res) {
        pdfBlob.value = `data:application/pdf;base64,${res.data.data}`;
      }
    };

    const createReconfirmationSds = (sds: SdsResponse): ReconfirmationSds => {
      // ステータスに応じて表示するコメント情報を切り替える
      let comment = '';
      let comment_created_at = '';
      if (
        sds.status_id === StatusId.ERROR.value &&
        sds.assistant_log_cannot_analysis &&
        sds.assistant_log_cannot_analysis.length > 0
      ) {
        const recentCannotAnalysis =
          sds.assistant_log_cannot_analysis.reverse()[0];
        comment = recentCannotAnalysis.comment;
        comment_created_at = recentCannotAnalysis.created_at;
      } else if (
        sds.status_id === StatusId.ERROR_RECONFIRMATION.value &&
        sds.assistant_log_reconfirmation &&
        sds.assistant_log_reconfirmation.length > 0
      ) {
        const recentReconfirmation =
          sds.assistant_log_reconfirmation.reverse()[0];
        comment = recentReconfirmation.comment;
        comment_created_at = recentReconfirmation.created_at;
      }

      return {
        ...sds,
        sds_section14: sds.sds_section14 || {
          id: 0,
          sds_id: '',
          un_number_list: [],
          created_at: '',
          updated_at: '',
          deleted_at: null,
        }, // TODO:nullが返ってくることがあるが、型を直すと影響が大きすぎるため初期値設定としている
        comment: comment,
        comment_created_at: comment_created_at,
      };
    };

    const fetchCompanySds = async (sdsId: string): Promise<void> => {
      const res = await dispatchGetCompanySds(store, {
        sdsId: sdsId,
        companyId: readAssertedCompanyId(store),
      });

      if (res) {
        activeSds.value.tags = res.data.sds.tags;
        activeSds.value.id = res.data.sds.id;
        activeSds.value.uuid = res.data.sds.uuid;
        activeSds.value.name = res.data.sds.name;
        activeSds.value.replaced_name = res.data.sds.replaced_name;
        activeSds.value.note = res.data.sds.note;
        activeSds.value.admin_note = res.data.sds.admin_note;
        activeSds.value.company_id = res.data.sds.company_id;
        activeSds.value.departments = res.data.sds.departments;
        activeSds.value.status_id = res.data.sds.status_id;
        activeSds.value.updated_by = res.data.sds.updated_by;
        activeSds.value.timestamp = res.data.sds.timestamp;
        activeSds.value.published_at = res.data.sds.published_at;
        activeSds.value.revised_at = res.data.sds.revised_at;
        activeSds.value.created_at = res.data.sds.created_at;
        activeSds.value.updated_at = res.data.sds.updated_at;
        activeSds.value.supplier_confirmed_at =
          res.data.sds.supplier_confirmed_at;
        activeSds.value.deleted_at = res.data.sds.deleted_at;
        activeSds.value.assessed_at = res.data.sds.assessed_at;
        activeSds.value.sds_section14 = res.data.sds.sds_section14;
        activeSds.value.sds_section8 = res.data.sds.sds_section8;
        activeSds.value.dynamic_list_result = res.data.sds.dynamic_list_result;
        activeSds.value.sds_products = res.data.sds.sds_products.map((x) => {
          return {
            id: x.id,
            sds_id: x.sds_id,
            name: x.name,
            created_at: x.created_at,
            updated_at: x.updated_at,
            deleted_at: x.deleted_at,
            sds_section1_importers: x.sds_section1_importers.map((im) => {
              return {
                id: im.id,
                sds_id: im.sds_id,
                sds_product_id: im.sds_product_id,
                name: im.name,
                phone: im.phone,
                address: im.address,
                email: im.email,
                created_at: im.created_at,
                updated_at: im.updated_at,
                deleted_at: im.deleted_at,
              };
            }),
            sds_section1_manufacturers: x.sds_section1_manufacturers.map(
              (im) => {
                return {
                  id: im.id,
                  sds_id: im.sds_id,
                  sds_product_id: im.sds_product_id,
                  name: im.name,
                  phone: im.phone,
                  address: im.address,
                  email: im.email,
                  created_at: im.created_at,
                  updated_at: im.updated_at,
                  deleted_at: im.deleted_at,
                };
              }
            ),
            sds_section2_list: x.sds_section2_list.sort((a, b) =>
              a.item_id > b.item_id ? 1 : -1
            ),
            sds_section2_label: {
              id: x.sds_section2_label.id,
              sds_id: x.sds_section2_label.sds_id,
              sds_product_id: x.sds_section2_label.sds_product_id,
              bomb: x.sds_section2_label.bomb,
              fire: x.sds_section2_label.fire,
              fire_circle: x.sds_section2_label.fire_circle,
              gas: x.sds_section2_label.gas,
              corrosion: x.sds_section2_label.corrosion,
              toxic: x.sds_section2_label.toxic,
              exclamation: x.sds_section2_label.exclamation,
              health: x.sds_section2_label.health,
              environment: x.sds_section2_label.environment,
              signal_words: x.sds_section2_label.signal_words,
              signal_word_id: x.sds_section2_label.signal_word_id,
              created_at: x.sds_section2_label.created_at,
              updated_at: x.sds_section2_label.updated_at,
              deleted_at: x.sds_section2_label.deleted_at,
            },
            sds_section3: {
              id: x.sds_section3.id,
              sds_id: x.sds_section3.sds_id,
              sds_product_id: x.sds_section3.sds_product_id,
              created_at: x.sds_section3.created_at,
              updated_at: x.sds_section3.updated_at,
              deleted_at: x.sds_section3.deleted_at,
              composition_id: x.sds_section3.composition_id,
              synonyms: x.sds_section3.synonyms,
              aliases: x.sds_section3.aliases,
              chemical_properties: x.sds_section3.chemical_properties,
              material_list: x.sds_section3.material_list.map((s3) => {
                return {
                  id: s3.id,
                  sds_id: s3.sds_id,
                  sds_section3_id: s3.sds_section3_id,
                  material_name: s3.material_name,
                  concentration: s3.concentration,
                  encs_number: s3.encs_number,
                  ishl_number: s3.ishl_number,
                  is_stated: s3.is_stated,
                  concentration_inequality_sign:
                    s3.concentration_inequality_sign,
                  concentration_min: s3.concentration_min,
                  concentration_max: s3.concentration_max,
                  concentration_unit: s3.concentration_unit,
                  created_at: s3.created_at,
                  updated_at: s3.updated_at,
                  deleted_at: s3.deleted_at,
                  cas_list: s3.cas_list.map((cas) => {
                    return {
                      id: cas.id,
                      sds_id: cas.sds_id,
                      sds_section3_material_id: cas.sds_section3_material_id,
                      cas_number: cas.cas_number,
                      created_at: cas.created_at,
                      updated_at: cas.updated_at,
                      deleted_at: cas.deleted_at,
                    };
                  }),
                };
              }),
            },
            sds_section8_exposure_control_list:
              x.sds_section8_exposure_control_list,
            sds_section9_list: x.sds_section9_list.map((s9) => {
              return {
                id: s9.id,
                sds_id: s9.sds_id,
                sds_product_id: s9.sds_product_id,
                condition: s9.condition,
                physical_condition: s9.physical_condition,
                form: s9.form,
                created_at: s9.created_at,
                updated_at: s9.updated_at,
                deleted_at: s9.deleted_at,
              };
            }),
            sds_section9_flash_point_list: x.sds_section9_flash_point_list.map(
              (f) => {
                return {
                  id: f.id,
                  sds_id: f.sds_id,
                  sds_product_id: f.sds_product_id,
                  flash_point_min: f.flash_point_min,
                  flash_point_max: f.flash_point_max,
                  flash_point_inequality_sign: f.flash_point_inequality_sign,
                  unit_id: f.unit_id,
                  measurement_method: f.measurement_method,
                  created_at: f.created_at,
                  updated_at: f.updated_at,
                  deleted_at: f.deleted_at,
                };
              }
            ),
            sds_section9_boiling_point_list:
              x.sds_section9_boiling_point_list.map((f) => {
                return {
                  id: f.id,
                  sds_id: f.sds_id,
                  sds_product_id: f.sds_product_id,
                  boiling_point_min: f.boiling_point_min,
                  boiling_point_max: f.boiling_point_max,
                  boiling_point_inequality_sign:
                    f.boiling_point_inequality_sign,
                  unit_id: f.unit_id,
                  measurement_method: f.measurement_method,
                  created_at: f.created_at,
                  updated_at: f.updated_at,
                  deleted_at: f.deleted_at,
                };
              }),
            sds_section15_list: x.sds_section15_list.map((section15) => {
              return {
                id: section15.id,
                sds_id: section15.sds_id,
                sds_product_id: section15.sds_product_id,
                law_id: section15.law_id,
                regulation1_id: section15.regulation1_id,
                regulation2_id: section15.regulation2_id,
                regulation3_id: section15.regulation3_id,
                regulation4_id: section15.regulation4_id,
                regulation5_id: section15.regulation5_id,
                applicable_id: section15.applicable_id,
                text: section15.text,
                created_at: section15.created_at,
                updated_at: section15.updated_at,
                deleted_at: section15.deleted_at,
                material_list: section15.material_list.map((material) => {
                  return {
                    id: material.id,
                    sds_id: material.sds_id,
                    sds_section15_id: material.sds_section15_id,
                    material_name: material.material_name,
                    created_at: material.created_at,
                    updated_at: material.updated_at,
                    deleted_at: material.deleted_at,
                    cas_list: material.cas_list.map((cas) => {
                      return {
                        id: cas.id,
                        sds_id: cas.sds_id,
                        sds_section15_material_id:
                          cas.sds_section15_material_id,
                        cas_number: cas.cas_number,
                        created_at: cas.created_at,
                        updated_at: cas.updated_at,
                        deleted_at: cas.deleted_at,
                      };
                    }),
                  };
                }),
              };
            }),
          };
        });
      }

      if (res) {
        bindSdsData(createReconfirmationSds(res.data.sds));
      }
    };

    const fetchCompanySdses = async (): Promise<void> => {
      reconfirmationSds.value.splice(0);

      const res = await dispatchGetCompanySdses(store, {
        companyId: readAssertedCompanyId(store),
        data: {
          limit: limit.value,
          page: page.value - 1,
          keyword_list: keywordList.value,
          status_id_list: [
            StatusId.ERROR.value,
            StatusId.ERROR_RECONFIRMATION.value,
          ],
          sort_state: { status: 'desc' }, // 要確認を先頭に表示する
          user_id_list: [],
          tag_id_list: [],
          department_id_list: [],
          sds_name: '',
          product_name: '',
          revised_at_start_date: '',
          revised_at_end_date: '',
          created_at_start_date: '',
          created_at_end_date: '',
          has_product: true,
          has_section_1: false,
          has_section_2: false,
          has_section_3: true,
          has_section_8: false,
          has_section_8_exposure_control: false,
          has_section_9: false,
          has_section_14: true,
          has_section_15: false,
          has_assistant_log_cannot_analysis: true,
          has_assistant_log_reconfirmation: true,
        },
      });
      if (res) {
        reconfirmationSds.value.splice(0);
        reconfirmationSds.value.push(
          ...res.data.sdses.map((sds) => createReconfirmationSds(sds))
        );
      }
    };

    const fetchCompanySdsesCountWithCondition = async (): Promise<void> => {
      isLoadingSdses.value = true;

      const res = await dispatchGetCompanySdsesCount(store, {
        companyId: readAssertedCompanyId(store),
        data: {
          keyword_list: keywordList.value,
          status_id_list: [
            StatusId.ERROR.value,
            StatusId.ERROR_RECONFIRMATION.value,
          ],
        },
      });
      if (res) {
        total.value = res.data.total;
      }

      isLoadingSdses.value = false;
    };

    const fetchCompanySdsesCount = async (): Promise<void> => {
      const res = await dispatchGetCompanySdsesCount(store, {
        companyId: readAssertedCompanyId(store),
        data: {
          keyword_list: keywordList.value,
        },
      });
      if (res) {
        commitSetCompany(store, {
          ...readAssertedCompany(store),
          uploaded_file_number: res.data.total,
        });
      }
    };

    // 編集可能なSDS項目のみ更新する
    const doUpdateCompanySds = async (
      data: ReconfirmationSds,
      tags: TagResponse[],
      departments: Department[]
    ): Promise<void> => {
      if (StatusId.updatableValues().includes(data.status_id)) {
        await updateCompanySds(
          readAssertedCompanyId(store),
          data.uuid,
          data,
          departments,
          tags,
          store.state.main.userProfile
        );
      } else {
        await dispatchUpdateReconfirmationSds(store, {
          companyId: readAssertedCompanyId(store),
          sdsId: data.uuid,
          form: {
            department_id_list: departments.map((v) => v.uuid),
            tag_id_list: tags.map((v) => v.id),
            assignee_user_id_list: data.assignee_users.map((v) => v.uuid),
            note: data.note,
            assessed_at: data.assessed_at,
            supplier_confirmed_at: data.supplier_confirmed_at,
            attachments: data.attachments,
          },
        });
      }
      await fetchCompanySdses();
      await fetchCompanySdsesCount();
      await fetchCompanySdsesCountWithCondition();
      await fetchCompanySds(data.uuid);
    };

    const deleteCompanySds = async (uuidList: Array<string>): Promise<void> => {
      const res = await dispatchDeleteCompanySdses(store, {
        companyId: readAssertedCompanyId(store),
        data: {
          sds_uuid_list: uuidList,
        },
      });
      if (res) {
        await fetchCompanySdses();
        await fetchCompanySdsesCount();
        await fetchCompanySdsesCountWithCondition();
      }
    };

    const deleteSelectedSds = async (): Promise<void> => {
      const uuidList = selectedSdses.value.map((s) => s.uuid);
      await deleteCompanySds(uuidList);
    };

    const deleteSds = async (fileUuid: string): Promise<void> => {
      await deleteCompanySds([fileUuid]);
    };

    const confirmSave = (event): void => {
      if (isEditing.value) {
        event.preventDefault();
        event.returnValue = $t(sharedMessages.confirmLeave);
      }
    };

    onMounted(async (): Promise<void> => {
      window.onbeforeunload = confirmSave;
      await fetchCompanySdses();
      await fetchCompanySdsesCount();
      await fetchCompanySdsesCountWithCondition();
      await getCompanyDepartments();
      await getCompanyUsers({ includeSwitchable: true });
    });

    return {
      statusOptions,
      keywordList,
      pdfBlob,
      pdfName,
      limit,
      total,
      page,
      statusList,
      pdfDisplayOptions,
      reconfirmationSds,
      selectedSdses,
      isSelectedAllSdses,
      isSelectedIndeterminateSdses,
      toggleSelectedSds,
      toggleAllSelectedSdses,
      resetSelectedSdses,
      isLoadingSdses,
      isLoadingSds,
      isEditing,
      showSdsDetailView,
      productFormItems,
      showPrevSds,
      showNextSds,
      hasPrevSds,
      hasNextSds,
      showSdsDetail,
      deleteSelectedSds,
      deleteSds,
      resetPagenation,
      resetSearchForm,
      fetchCompanySdses,
      fetchCompanySdsesCount,
      fetchCompanySdsesCountWithCondition,
      fetchCompanySds,
      fetchPdfData,
      doUpdateCompanySds,
      activeSds,
      companyDepartments,
      visibleCompanyDepartmentsByOwnOrRole,
      sharedMessages,
      m,
      users,
    };
  },
});
